import api from './api';
import type { ApiResponse, TelephonyCredentials, TelephonyProviderOption, TransferAgent } from '@/types';

export const getTelephonyCredentials = async (): Promise<TelephonyCredentials> => {
  const response = await api.get<ApiResponse<TelephonyCredentials>>('/telephony/credentials');
  return response.data.data;
};

/**
 * Coerce the raw `telephony_providers_list` setting value into a clean list of
 * provider options. The backend stores this as a JSON array of
 * { id, label, prefix }; the settings API may hand it back as either an array
 * or a JSON string. Malformed/empty input yields [] (feature stays inert).
 */
export const parseTelephonyProviders = (raw: unknown): TelephonyProviderOption[] => {
  let value: unknown = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (entry): entry is { id: string; label: string; prefix: string } =>
        !!entry &&
        typeof entry === 'object' &&
        typeof (entry as { id?: unknown }).id === 'string' &&
        typeof (entry as { label?: unknown }).label === 'string' &&
        typeof (entry as { prefix?: unknown }).prefix === 'string'
    )
    .map((entry) => ({ id: entry.id, label: entry.label, prefix: entry.prefix }));
};

/**
 * Load the configured outbound telephony providers. Reads the auth-only
 * reference endpoint — NOT the settings group, which is behind
 * settings.general and 403s for agents (the picker would silently vanish
 * for exactly the users who need it). Returns [] when none are configured
 * or on any read error, so callers can render nothing.
 */
export const getTelephonyProviders = async (): Promise<TelephonyProviderOption[]> => {
  try {
    const response = await api.get<ApiResponse<unknown>>('/telephony/providers');
    return parseTelephonyProviders(response.data.data);
  } catch {
    return [];
  }
};

/**
 * Persist the current agent's sticky outbound-provider choice. Self-scoped
 * (no {user} param) so any authenticated agent can set their own preference —
 * mirrors `/auth/me/locale`. Sends only the provider id (never a raw prefix).
 */
export const updateMyTelephonyProvider = async (
  providerId: string | null
): Promise<void> => {
  await api.patch('/auth/me/telephony-provider', { telephony_provider_id: providerId });
};

export const getTransferAgents = async (): Promise<TransferAgent[]> => {
  const response = await api.get<ApiResponse<TransferAgent[]>>('/telephony/transfer-agents');
  const agents = response.data.data;
  // Compute full_name client-side if not returned by backend
  return agents.map((agent) => ({
    ...agent,
    full_name: agent.full_name || `${agent.first_name} ${agent.last_name}`,
  }));
};
